import { useState, useEffect, useCallback, useMemo, type ReactNode } from 'react';
import { format, subDays, isSameDay } from 'date-fns';
import { MoonStar, Phone, X } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useApp } from '@/contexts/AppContext';
import SectionAccordionCard from '@/components/SectionAccordionCard';
import {
  AppButton,
  AppButtonRow,
  AppCard,
  AppChipButton,
  AppLoadingState,
  AppText,
  AppTextArea,
} from '../../../ui-foundation';

interface Checkin {
  id: string;
  mood: number;
  note: string | null;
  checked_in_at: string;
}

type BurnoutLevel = 'steady' | 'watch' | 'high';

type GuiltReframeKey =
  | 'missed-moment'
  | 'snappy-day'
  | 'money-pressure'
  | 'running-on-empty';

const MOODS = [
  { value: 1, emoji: '😔', label: 'Really struggling' },
  { value: 2, emoji: '😕', label: 'Not great' },
  { value: 3, emoji: '😐', label: 'Okay' },
  { value: 4, emoji: '🙂', label: 'Good' },
  { value: 5, emoji: '😊', label: 'Great' },
];

const MOOD_COLOURS = ['', 'bg-red-400', 'bg-orange-400', 'bg-yellow-400', 'bg-lime-400', 'bg-green-400'];

const CRISIS_LINES = [
  { name: 'Samaritans', number: '116 123', note: 'Free, 24/7' },
  { name: 'Mind', number: '0300 123 3393', note: 'Mon–Fri 9am–6pm' },
  { name: 'NHS urgent mental health', number: '111', note: 'Press option 2' },
];

const SUPPORT_PLAYBOOKS = [
  {
    title: 'Reset tonight',
    description: 'Shrink the evening. Pick one tiny admin job or none at all, then stop.',
    points: ['Drink water and sit down before opening anything difficult.', 'Write tomorrow’s first step in one sentence.', 'Leave the rest for daylight.'],
  },
  {
    title: 'Protect this week',
    description: 'If your load is creeping up, lower the standard before you disappear under it.',
    points: ['Move one non-urgent task to next week now.', 'Tell one person what is actually feeling heavy.', 'Use Adulting for one guided task instead of carrying the whole list in your head.'],
  },
  {
    title: 'Ask for help sooner',
    description: 'You do not need to look fully overwhelmed before support counts.',
    points: ['Book or chase the appointment.', 'Ask for an extension or repayment conversation early.', 'Use support lines if today feels unsafe or unmanageable.'],
  },
];

const GUILT_REFRAMES: Array<{
  key: GuiltReframeKey;
  label: string;
  commonThought: string;
  kinderRead: string;
  repairStep: string;
}> = [
  {
    key: 'missed-moment',
    label: 'Missed something important',
    commonThought: 'I missed the thing that mattered, so I have let them down.',
    kinderRead: 'One missed moment hurts because you care. Children usually need repair, steadiness, and being thought about again more than one flawless appearance record.',
    repairStep: 'Name it plainly, apologise if needed, and make the next specific moment visible: "I hated missing that. Tell me the best part, and let us put the next one in together now."',
  },
  {
    key: 'snappy-day',
    label: 'I was short with people',
    commonThought: 'If I was snappy, I have ruined the whole day and become the parent or partner I did not want to be.',
    kinderRead: 'A hard moment is not your whole character. Repair after stress teaches more than pretending you never get overwhelmed.',
    repairStep: 'Own the tone without excuses, then reset one thing: "I was sharp earlier. That was mine. Let me try this again more calmly."',
  },
  {
    key: 'money-pressure',
    label: 'Money made today smaller',
    commonThought: 'If money is tight, I am failing the people who rely on me.',
    kinderRead: 'Financial pressure is a load problem, not a worth problem. Protection, planning, and asking for support are responsible acts, not proof that you are behind as a person.',
    repairStep: 'Pick one stabilising step only: a benefits re-check, a bill call, or one payment reminder. You do not need to solve the whole month tonight.',
  },
  {
    key: 'running-on-empty',
    label: 'I had nothing left',
    commonThought: 'If I am this tired and flat, everyone else is carrying the cost of me falling short.',
    kinderRead: 'Running on empty changes capacity; it does not erase care. Rest and lowering the standard are often how you protect people, not how you fail them.',
    repairStep: 'Cut one non-essential task, say out loud what can wait, and make the next need smaller instead of trying to earn rest afterwards.',
  },
];

type WellbeingSectionKey =
  | 'check-in'
  | 'this-week'
  | 'late-night'
  | 'burnout'
  | 'next'
  | 'guilt'
  | 'vent'
  | 'support'
  | 'routes'
  | 'council';

const WELLBEING_SECTION_EMOJI: Record<WellbeingSectionKey, string> = {
  'check-in': '🙂',
  'this-week': '📈',
  'late-night': '🌙',
  burnout: '⚠️',
  next: '🪴',
  guilt: '💛',
  vent: '📝',
  support: '🆘',
  routes: '🧑‍⚕️',
  council: '🏛️',
};

function WellbeingSection({
  emoji,
  title,
  subtitle: _subtitle,
  open,
  onToggle,
  children,
}: {
  emoji: string;
  title: string;
  subtitle: string;
  open: boolean;
  onToggle: () => void;
  children: ReactNode;
}) {
  return (
    <SectionAccordionCard emoji={emoji} title={title} subtitle={_subtitle} open={open} onToggle={onToggle}>
      <div className="space-y-4">{children}</div>
    </SectionAccordionCard>
  );
}

function getBurnoutState(checkins: Checkin[], todayCheckin: Checkin | null) {
  if (checkins.length === 0) {
    return {
      level: 'steady' as BurnoutLevel,
      title: 'Build a signal first',
      summary: 'A few check-ins will help Adulting spot when stress is turning into burnout.',
      reasons: ['There is not enough recent check-in data yet to spot a pattern.'],
    };
  }

  const lowMoodCount = checkins.filter((entry) => entry.mood <= 2).length;
  const averageMood = checkins.reduce((sum, entry) => sum + entry.mood, 0) / checkins.length;
  const recentLowStreak = checkins.slice(0, 3).filter((entry) => entry.mood <= 2).length;
  const reasons: string[] = [];

  if (todayCheckin && todayCheckin.mood <= 2) {
    reasons.push('Today you marked yourself as struggling.');
  }

  if (lowMoodCount >= 3) {
    reasons.push('Several recent check-ins have landed in the low range.');
  }

  if (averageMood <= 2.5) {
    reasons.push('Your average mood this week has dropped below your steadier range.');
  }

  if (recentLowStreak >= 2) {
    reasons.push('The last few check-ins suggest the pressure is stacking, not just blipping.');
  }

  if (reasons.length >= 2) {
    return {
      level: 'high' as BurnoutLevel,
      title: 'Burnout risk looks high',
      summary: 'This looks like more than an off day. Aim smaller, cut something, and get support in sooner.',
      reasons,
    };
  }

  if (reasons.length === 1) {
    return {
      level: 'watch' as BurnoutLevel,
      title: 'Worth watching this week',
      summary: 'Something in your check-ins suggests the load is edging up. Protect the next few days now.',
      reasons,
    };
  }

  return {
    level: 'steady' as BurnoutLevel,
    title: 'Your check-ins look steadier right now',
    summary: 'Keep using short check-ins so Adulting can spot a rough patch early, not after you crash.',
    reasons: ['No obvious burnout pattern is showing in your recent check-ins.'],
  };
}

function burnoutToneProps(level: BurnoutLevel) {
  if (level === 'high') return { borderColor: '$destructive', backgroundColor: '$destructiveTint' } as const;
  if (level === 'watch') return { borderColor: '$accent', backgroundColor: '$accentTint' } as const;
  return { borderColor: '$borderColor', backgroundColor: '$cardBackground' } as const;
}

function notifyAutomationRefresh() {
  window.dispatchEvent(new CustomEvent('adulting:data-changed'));
}

export default function WellbeingTab() {
  const { user, data, refreshData } = useApp();
  const [recentCheckins, setRecentCheckins] = useState<Checkin[]>([]);
  const [todayCheckin, setTodayCheckin] = useState<Checkin | null>(null);
  const [loading, setLoading] = useState(true);

  const [selectedMood, setSelectedMood] = useState<number | null>(null);
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);
  const [showNoteField, setShowNoteField] = useState(false);
  const [showUpdate, setShowUpdate] = useState(false);

  const [burnoutDismissed, setBurnoutDismissed] = useState(false);
  const [showSupport, setShowSupport] = useState(false);

  const [ventText, setVentText] = useState('');
  const [ventExpanded, setVentExpanded] = useState(false);
  const [selectedGuiltReframe, setSelectedGuiltReframe] = useState<GuiltReframeKey>('missed-moment');
  const [openSections, setOpenSections] = useState<Record<WellbeingSectionKey, boolean>>({
    'check-in': false,
    'this-week': false,
    'late-night': false,
    'burnout': false,
    'next': false,
    'guilt': false,
    'vent': false,
    'support': false,
    'routes': false,
    council: false,
  });

  const fetchCheckins = useCallback(async () => {
    if (!user) return;

    const sevenDaysAgo = subDays(new Date(), 7).toISOString();
    const { data } = await supabase
      .from('wellbeing_checkins')
      .select('*')
      .eq('user_id', user.id)
      .gte('checked_in_at', sevenDaysAgo)
      .order('checked_in_at', { ascending: false });

    const checkins = (data as Checkin[]) || [];
    setRecentCheckins(checkins);
    setTodayCheckin(checkins.find((entry) => isSameDay(new Date(entry.checked_in_at), new Date())) || null);
    setLoading(false);
  }, [user]);

  useEffect(() => {
    void fetchCheckins();
  }, [fetchCheckins]);

  const handleMoodTap = (mood: number) => {
    setSelectedMood(mood);
    setShowNoteField(true);
  };

  const handleSave = async () => {
    if (!user || !selectedMood) return;
    setSaving(true);

    if (todayCheckin && showUpdate) {
      await supabase
        .from('wellbeing_checkins')
        .update({ mood: selectedMood, note: note.trim() || null } as any)
        .eq('id', todayCheckin.id);
    } else {
      await supabase.from('wellbeing_checkins').insert({
        user_id: user.id,
        mood: selectedMood,
        note: note.trim() || null,
      } as any);
    }

    setSaving(false);
    setShowNoteField(false);
    setShowUpdate(false);
    setSelectedMood(null);
    setNote('');
    notifyAutomationRefresh();
    await fetchCheckins();
  };

  const weekDays = Array.from({ length: 7 }, (_, index) => {
    const day = subDays(new Date(), 6 - index);
    const checkin = recentCheckins.find((entry) => isSameDay(new Date(entry.checked_in_at), day));
    return { day, checkin };
  });
  const isCheckingIn = showNoteField || showUpdate;
  const averageMood = recentCheckins.length
    ? Number((recentCheckins.reduce((sum, entry) => sum + entry.mood, 0) / recentCheckins.length).toFixed(1))
    : null;
  const lowMoodCount = recentCheckins.filter((entry) => entry.mood <= 2).length;
  const burnoutState = getBurnoutState(recentCheckins, todayCheckin);
  const hour = new Date().getHours();
  const lateNightMode = hour >= 22 || hour < 6;
  const guiltReframe = GUILT_REFRAMES.find((item) => item.key === selectedGuiltReframe) || GUILT_REFRAMES[0];
  const professionalRoutes = useMemo(
    () => data.communityDirectoryEntries.slice(0, 6),
    [data.communityDirectoryEntries],
  );
  const localCouncilSupport = useMemo(
    () => data.communityPolicyUpdates.filter((entry) => entry.origin === 'local-scheme').slice(0, 6),
    [data.communityPolicyUpdates],
  );

  useEffect(() => {
    if (burnoutState.level === 'high' && !burnoutDismissed) {
      setOpenSections((current) => ({
        ...current,
        burnout: true,
        support: true,
      }));
    }
  }, [burnoutDismissed, burnoutState.level]);

  const toggleSection = useCallback((section: WellbeingSectionKey) => {
    setOpenSections((current) => ({
      ...current,
      [section]: !current[section],
    }));
  }, []);

  if (loading) {
    return <AppLoadingState title="Loading wellbeing" body="Pulling your recent check-ins, burnout cues, and support prompts." />;
  }

  return (
    <div className="space-y-4">
      <WellbeingSection
        emoji={WELLBEING_SECTION_EMOJI['check-in']}
        title="Wellbeing Check In"
        subtitle="Log a quick signal and keep it inside Adulting."
        open={openSections['check-in']}
        onToggle={() => toggleSection('check-in')}
      >
        {todayCheckin && !showUpdate ? (
          <div className="text-center">
            <AppText className="mb-2" fontSize="$sm" color="$colorMuted">You checked in today</AppText>
            <p className="text-4xl mb-1">{MOODS[todayCheckin.mood - 1]?.emoji}</p>
            <AppText fontSize="$sm" fontWeight="700">{MOODS[todayCheckin.mood - 1]?.label}</AppText>
            {todayCheckin.note && (
              <AppText className="mt-2 italic" fontSize="$xs" color="$colorMuted">"{todayCheckin.note}"</AppText>
            )}
            <AppButton
              className="mt-3"
              size="sm"
              tone="ghost"
              onPress={() => {
                setShowUpdate(true);
                setSelectedMood(todayCheckin.mood);
                setNote(todayCheckin.note || '');
                setShowNoteField(true);
              }}
            >
              Update
            </AppButton>
          </div>
        ) : (
          <>
            <AppText className="mb-3 text-center" fontSize="$sm" fontWeight="700">How are you doing today?</AppText>
            <div className="flex justify-center gap-3 mb-2">
              {MOODS.map((mood) => (
                <AppChipButton
                  key={mood.value}
                  active={selectedMood === mood.value}
                  onClick={() => handleMoodTap(mood.value)}
                  className={`h-auto flex-col gap-1 px-3 py-2 ${
                    selectedMood === mood.value ? 'scale-110' : 'opacity-70 hover:opacity-100'
                  } ${selectedMood && selectedMood !== mood.value ? 'opacity-40' : ''}`}
                >
                  <span className="text-2xl">{mood.emoji}</span>
                  <AppText fontSize="$xs" color="$colorMuted">{mood.value}</AppText>
                </AppChipButton>
              ))}
            </div>

            {isCheckingIn && (
              <div className="mt-4 space-y-3 animate-fade-in">
                <AppTextArea
                  value={note}
                  onChange={(event) => setNote((event.target as HTMLTextAreaElement).value)}
                  placeholder="Want to add anything? (optional)"
                  rows={2}
                />
                <AppButtonRow>
                  <AppButton
                    onPress={handleSave}
                    disabled={saving}
                    flex={1}
                  >
                    {saving ? 'Saving…' : 'Save'}
                  </AppButton>
                  <AppButton
                    tone="outline"
                    onPress={() => {
                      setShowNoteField(false);
                      setShowUpdate(false);
                      setSelectedMood(null);
                      setNote('');
                    }}
                  >
                    Cancel
                  </AppButton>
                </AppButtonRow>
              </div>
            )}
          </>
        )}
      </WellbeingSection>

      <WellbeingSection
        emoji={WELLBEING_SECTION_EMOJI['this-week']}
        title="This week"
        subtitle="A quick read on the last seven days so you can spot what is building."
        open={openSections['this-week']}
        onToggle={() => toggleSection('this-week')}
      >
      <AppCard backgroundColor="$background" px="$md" py="$md">
        <div className="flex items-center justify-between gap-3 mb-3">
          <AppText fontSize="$xs" fontWeight="700" textTransform="uppercase" letterSpacing={0.8} color="$colorMuted">This week</AppText>
          <AppText fontSize="$xs" color="$colorMuted">
            {averageMood ? `Average mood ${averageMood}/5` : 'No average yet'}
          </AppText>
        </div>
        <div className="flex justify-between items-end gap-1">
          {weekDays.map(({ day, checkin }, index) => (
            <div key={index} className="flex flex-col items-center gap-1.5 flex-1">
              <div
                className={`w-5 h-5 rounded-full ${checkin ? MOOD_COLOURS[checkin.mood] : 'bg-muted'}`}
                title={checkin ? `${MOODS[checkin.mood - 1]?.label}` : 'No check-in'}
              />
              <AppText fontSize="$xs" color="$colorMuted">{format(day, 'EEE').slice(0, 2)}</AppText>
            </div>
          ))}
        </div>
        <div className="flex justify-between gap-3 mt-4">
          <AppText fontSize="$xs" color="$colorMuted">{recentCheckins.length} check-in{recentCheckins.length === 1 ? '' : 's'}</AppText>
          <AppText fontSize="$xs" color="$colorMuted">{lowMoodCount} low-mood day{lowMoodCount === 1 ? '' : 's'}</AppText>
        </div>
      </AppCard>
      </WellbeingSection>

      {lateNightMode && (
        <WellbeingSection
          emoji={WELLBEING_SECTION_EMOJI['late-night']}
          title="Late-night mode"
          subtitle="Keep the late-evening advice there when you need it, but out of the way the rest of the time."
          open={openSections['late-night']}
          onToggle={() => toggleSection('late-night')}
        >
          <div className="flex items-center gap-2">
            <MoonStar size={16} className="text-primary" />
            <AppText fontSize="$sm" fontWeight="700">Late-night mode</AppText>
          </div>
          <AppText fontSize="$xs" color="$colorMuted">
            It is easier to catastrophise life admin late at night. Keep tonight tiny and leave the hard calls for daylight.
          </AppText>
          <AppCard backgroundColor="$background" px="$md" py="$sm">
            <AppText fontSize="$xs" fontWeight="700">Try this before opening another task:</AppText>
            <ul className="space-y-1.5 text-xs text-muted-foreground leading-relaxed">
              <li>• Pick one thing to park until tomorrow morning.</li>
              <li>• If you still need to do something, make it admin-light: upload a document, draft a note, or set one reminder.</li>
              <li>• If the load feels unsafe or unmanageable, use proper support tonight instead of pushing through.</li>
            </ul>
          </AppCard>
        </WellbeingSection>
      )}

      {!burnoutDismissed && (
        <WellbeingSection
          emoji={WELLBEING_SECTION_EMOJI.burnout}
          title={burnoutState.title}
          subtitle={burnoutState.summary}
          open={openSections['burnout']}
          onToggle={() => toggleSection('burnout')}
        >
        <AppCard {...burnoutToneProps(burnoutState.level)}>
          <div className="flex items-center justify-between gap-3">
            <div>
              <AppText fontSize="$sm" fontWeight="700">What Adulting is seeing</AppText>
              <AppText className="mt-1" fontSize="$xs" color="$colorMuted">A quick read of the recent pattern, so you can act earlier.</AppText>
            </div>
            <span className={`rounded-full px-2.5 py-1 text-[10px] font-semibold ${
              burnoutState.level === 'high'
                ? 'bg-rose-100 text-rose-800'
                : burnoutState.level === 'watch'
                  ? 'bg-amber-100 text-amber-800'
                  : 'bg-primary/10 text-primary'
            }`}>
              {burnoutState.level === 'high' ? 'High risk' : burnoutState.level === 'watch' ? 'Watch' : 'Steady'}
            </span>
          </div>

          <div className="space-y-2">
            {burnoutState.reasons.map((reason) => (
              <div key={reason} className="flex items-start gap-2">
                <span className="mt-0.5 text-muted-foreground">•</span>
                <AppText fontSize="$xs">{reason}</AppText>
              </div>
            ))}
          </div>

          {showSupport ? (
            <div className="animate-fade-in space-y-3">
              <AppText fontSize="$xs" fontWeight="700">Free and confidential support:</AppText>
              {CRISIS_LINES.map((line) => (
                <a
                  key={line.name}
                  href={`tel:${line.number.replace(/\s/g, '')}`}
                  className="block transition-all active:scale-[0.98]"
                >
                  <AppCard backgroundColor="$background" px="$sm" py="$sm">
                    <div className="flex items-center gap-3">
                      <Phone size={16} className="text-primary shrink-0" />
                      <div className="flex-1">
                        <AppText fontSize="$sm" fontWeight="700">{line.name}</AppText>
                        <AppText fontSize="$xs" color="$colorMuted">{line.number} · {line.note}</AppText>
                      </div>
                    </div>
                  </AppCard>
                </a>
              ))}
              <AppButton
                tone="outline"
                onPress={() => {
                  setShowSupport(false);
                  setBurnoutDismissed(true);
                }}
              >
                Close
              </AppButton>
            </div>
          ) : (
            <AppButtonRow>
              <AppButton
                tone="outline"
                onPress={() => setBurnoutDismissed(true)}
                flex={1}
              >
                Dismiss for now
              </AppButton>
              <AppButton
                onPress={() => setShowSupport(true)}
                flex={1}
              >
                Show support options
              </AppButton>
            </AppButtonRow>
          )}
        </AppCard>
        </WellbeingSection>
      )}

      <WellbeingSection
        emoji={WELLBEING_SECTION_EMOJI.next}
        title="What helps next"
        subtitle="Short prompts for the next gentle step when the load is building."
        open={openSections['next']}
        onToggle={() => toggleSection('next')}
      >
        <div className="space-y-3">
          {SUPPORT_PLAYBOOKS.map((playbook) => (
            <AppCard key={playbook.title} backgroundColor="$background" px="$md" py="$sm">
              <AppText fontSize="$sm" fontWeight="700">{playbook.title}</AppText>
              <AppText fontSize="$xs" color="$colorMuted">{playbook.description}</AppText>
              <ul className="space-y-1.5 text-xs text-foreground leading-relaxed">
                {playbook.points.map((point) => (
                  <li key={point} className="flex items-start gap-2">
                    <span className="mt-0.5 text-muted-foreground">•</span>
                    <span>{point}</span>
                  </li>
                ))}
              </ul>
            </AppCard>
          ))}
        </div>
      </WellbeingSection>

      <WellbeingSection
        emoji={WELLBEING_SECTION_EMOJI.guilt}
        title="Guilt reframing"
        subtitle="Warm prompts for the moments when guilt gets louder than the facts."
        open={openSections['guilt']}
        onToggle={() => toggleSection('guilt')}
      >
        <AppButtonRow>
          {GUILT_REFRAMES.map((item) => (
            <AppButton
              key={item.key}
              size="sm"
              tone={item.key === guiltReframe.key ? 'primary' : 'outline'}
              onPress={() => setSelectedGuiltReframe(item.key)}
            >
              {item.label}
            </AppButton>
          ))}
        </AppButtonRow>

        <AppCard backgroundColor="$background" px="$md" py="$sm">
          <div>
            <AppText fontSize="$xs" fontWeight="700" textTransform="uppercase" letterSpacing={0.8} color="$colorMuted">What guilt says</AppText>
            <AppText className="mt-1" fontSize="$sm">{guiltReframe.commonThought}</AppText>
          </div>
          <div>
            <AppText fontSize="$xs" fontWeight="700" textTransform="uppercase" letterSpacing={0.8} color="$colorMuted">Kinder read</AppText>
            <AppText className="mt-1" fontSize="$sm">{guiltReframe.kinderRead}</AppText>
          </div>
          <div>
            <AppText fontSize="$xs" fontWeight="700" textTransform="uppercase" letterSpacing={0.8} color="$colorMuted">Try next</AppText>
            <AppText className="mt-1" fontSize="$sm">{guiltReframe.repairStep}</AppText>
          </div>
        </AppCard>
      </WellbeingSection>

      <WellbeingSection
        emoji={WELLBEING_SECTION_EMOJI.vent}
        title="Vent mode"
        subtitle="A private scratchpad when you just need to get the words out."
        open={openSections['vent']}
        onToggle={() => toggleSection('vent')}
      >
        <div className="flex items-center justify-between mb-2">
          <AppText fontSize="$sm" fontWeight="700">Vent mode</AppText>
          {ventExpanded && ventText && (
            <AppButton
              tone="ghost"
              size="sm"
              onPress={() => {
                setVentText('');
                setVentExpanded(false);
              }}
            >
              <X size={12} /> Clear
            </AppButton>
          )}
        </div>
        <AppText className="mb-3" fontSize="$xs" color="$colorMuted">
          This is just for you. Write whatever you need to. It will not be saved.
        </AppText>
        <AppTextArea
          value={ventText}
          onChange={(event) => setVentText((event.target as HTMLTextAreaElement).value)}
          onFocus={() => setVentExpanded(true)}
          placeholder="Let it out…"
          rows={ventExpanded ? 6 : 2}
        />
      </WellbeingSection>

      <WellbeingSection
        emoji={WELLBEING_SECTION_EMOJI.support}
        title="Support when today feels heavy"
        subtitle="If today feels unmanageable, use proper support. Adulting can help with life admin, but it is not crisis care."
        open={openSections['support']}
        onToggle={() => toggleSection('support')}
      >
        <div className="space-y-2">
          {CRISIS_LINES.map((line) => (
            <a
              key={line.name}
              href={`tel:${line.number.replace(/\s/g, '')}`}
              className="block transition-all active:scale-[0.98]"
            >
              <AppCard backgroundColor="$background" px="$md" py="$sm">
                <div className="flex items-center gap-3">
                  <Phone size={15} className="text-primary shrink-0" />
                  <div className="min-w-0">
                    <AppText fontSize="$sm" fontWeight="700">{line.name}</AppText>
                    <AppText fontSize="$xs" color="$colorMuted">{line.number} · {line.note}</AppText>
                  </div>
                </div>
              </AppCard>
            </a>
          ))}
        </div>
      </WellbeingSection>

      <WellbeingSection
        emoji={WELLBEING_SECTION_EMOJI.routes}
        title="Professional Support Routes"
        subtitle="Trusted professional routes kept close when peer support is not enough."
        open={openSections['routes']}
        onToggle={() => toggleSection('routes')}
      >
        {professionalRoutes.length === 0 ? (
          <AppEmptyState
            title="No professional routes yet"
            body="Trusted routes will appear here once they are active for your profile."
          />
        ) : (
          <div className="space-y-3">
            {professionalRoutes.map((entry) => (
              <AppCard key={entry.id} backgroundColor="$background" p="$sm">
                <div className="flex flex-wrap items-center gap-2">
                  <AppText fontSize="$sm" fontWeight="700">{entry.name}</AppText>
                  <span className="rounded-full border border-border bg-card px-2 py-0.5 text-[10px] font-medium text-muted-foreground">
                    {entry.category}
                  </span>
                  <span className="rounded-full border border-border bg-card px-2 py-0.5 text-[10px] font-medium text-muted-foreground">
                    {entry.locationScope || 'General'}
                  </span>
                </div>
                <AppText fontSize="$sm" className="mt-2 leading-relaxed">{entry.summary}</AppText>
                {entry.specialisms.length ? (
                  <div className="mt-2 flex flex-wrap gap-2">
                    {entry.specialisms.slice(0, 3).map((item) => (
                      <span key={`${entry.id}-${item}`} className="rounded-full bg-primary/10 px-2 py-0.5 text-[10px] font-medium text-primary">
                        {item}
                      </span>
                    ))}
                  </div>
                ) : null}
                <div className="mt-3 flex flex-wrap items-center gap-3 text-sm">
                  {entry.websiteUrl ? (
                    <a href={entry.websiteUrl} target="_blank" rel="noreferrer" className="text-primary underline underline-offset-2">
                      Open route
                    </a>
                  ) : null}
                  {entry.phone ? (
                    <a href={`tel:${entry.phone.replace(/\s/g, '')}`} className="text-primary underline underline-offset-2">
                      {entry.phone}
                    </a>
                  ) : null}
                </div>
              </AppCard>
            ))}
          </div>
        )}
      </WellbeingSection>

      <WellbeingSection
        emoji={WELLBEING_SECTION_EMOJI.council}
        title="Local Council Support"
        subtitle="Local council policy-watch and support cards tied to your saved postcode."
        open={openSections.council}
        onToggle={() => toggleSection('council')}
      >
        <AppButton tone="outline" size="sm" onClick={() => void refreshData()}>
          Refresh policy watch
        </AppButton>
        {!data.profile.postcode.trim() ? (
          <AppEmptyState
            title="Add a postcode first"
            body="Save your postcode in Profile so Adulting can keep local council support cards here."
          />
        ) : localCouncilSupport.length === 0 ? (
          <AppEmptyState
            title="No local council support cards yet"
            body="Adulting does not have any matched local council support cards ready for your saved postcode right now."
          />
        ) : (
          <div className="space-y-3">
            {localCouncilSupport.map((entry) => (
              <AppCard key={entry.id} backgroundColor="$background" p="$sm">
                <div className="flex flex-wrap items-center gap-2">
                  <AppText fontSize="$sm" fontWeight="700">{entry.title}</AppText>
                  {entry.localityLabel ? (
                    <span className="rounded-full border border-border bg-card px-2 py-0.5 text-[10px] font-medium text-muted-foreground">
                      {entry.localityLabel}
                    </span>
                  ) : null}
                </div>
                <AppText fontSize="$sm" className="mt-2 leading-relaxed">{entry.summary}</AppText>
                <AppText className="mt-2" fontSize="$xs" color="$colorMuted">
                  {entry.reasonLabel || entry.sourceName}
                </AppText>
                <div className="mt-3 flex flex-wrap items-center gap-3 text-sm">
                  <span className="text-muted-foreground">{entry.sourceName}</span>
                  <a href={entry.sourceUrl} target="_blank" rel="noreferrer" className="text-primary underline underline-offset-2">
                    {entry.linkLabel || 'Open source'}
                  </a>
                </div>
              </AppCard>
            ))}
          </div>
        )}
      </WellbeingSection>
    </div>
  );
}
